/**
 * React Hook for Google reCAPTCHA v3
 *
 * This hook loads the reCAPTCHA v3 script and provides a function
 * to execute reCAPTCHA verification.
 */

import { useEffect, useState, useCallback } from 'react';
import googleConfig from '../config/google.config';

const RECAPTCHA_SCRIPT_ID = 'recaptcha-script';

export const useRecaptcha = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);

  const { siteKey } = googleConfig.recaptcha;

  useEffect(() => {
    // Skip if not configured
    if (!siteKey || siteKey.includes('YOUR_')) {
      console.warn('reCAPTCHA not configured. Skipping initialization.');
      setError('reCAPTCHA not configured');
      return;
    }

    // Check if script already loaded
    if (document.getElementById(RECAPTCHA_SCRIPT_ID)) {
      setIsLoaded(true);
      return;
    }

    // Load reCAPTCHA script
    const script = document.createElement('script');
    script.id = RECAPTCHA_SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      setIsLoaded(true);
      console.log('✅ reCAPTCHA v3 loaded successfully');
    };

    script.onerror = () => {
      setError('Failed to load reCAPTCHA');
      console.error('❌ Failed to load reCAPTCHA script');
    };

    document.head.appendChild(script);

    // Cleanup
    return () => {
      const scriptElement = document.getElementById(RECAPTCHA_SCRIPT_ID);
      if (scriptElement) {
        scriptElement.remove();
      }
    };
  }, [siteKey]);

  /**
   * Execute reCAPTCHA verification
   * @param {string} action - The action name (e.g., 'contact_form', 'login', 'signup')
   * @returns {Promise<string>} - The reCAPTCHA token
   */
  const executeRecaptcha = useCallback(
    async (action = 'submit') => {
      if (!isLoaded) {
        throw new Error('reCAPTCHA not loaded yet');
      }

      if (!window.grecaptcha) {
        throw new Error('reCAPTCHA grecaptcha object not found');
      }

      try {
        const token = await window.grecaptcha.execute(siteKey, { action });
        return token;
      } catch (err) {
        console.error('reCAPTCHA execution error:', err);
        throw err;
      }
    },
    [isLoaded, siteKey]
  );

  return {
    isLoaded,
    error,
    executeRecaptcha,
  };
};

export default useRecaptcha;